/**
 * Temel bileşenler — Linear dili.
 *
 * Tasarım kararları:
 * · Yüzeyler 1px KENARLIKLA ayrılır, gölgeyle değil. Kart, satır ve buton
 *   ASLA gölge almaz; gölge yalnızca kaplama ve kip içindir.
 * · Kontroller 28px'tir ve 13px yazar. Hiyerarşi boyutla değil AĞIRLIKLA
 *   taşınır (400 / 450 / 510 / 590).
 * · Tek aksan: indigo. Birincil buton, açık anahtar, seçim ve odak halkası
 *   dışında hiçbir yerde görünmez.
 * · Üzerine gelme YALNIZCA zemini değiştirir — 80ms, dönüşüm yok, ölçek yok.
 * · Kasa modunda tüm dokunulabilir hedefler >= 64px (KK-307) — `kasa` bayrağı.
 *   Bu, Linear yoğunluğundan BİLİNÇLİ sapmadır ve pazarlık dışıdır.
 */
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import { forwardRef, useEffect, useId, useState } from "react";

// ---------------------------------------------------------------------------
// Buton
// ---------------------------------------------------------------------------

export type ButonTuru = "birincil" | "ikincil" | "tehlike" | "sessiz" | "onay";

export interface ButonOzellikleri extends ButtonHTMLAttributes<HTMLButtonElement> {
  tur?: ButonTuru;
  /** KK-307: kasa modunda hedef >= 64x64 px, yazı büyür */
  kasa?: boolean;
  tamGenislik?: boolean;
  yukleniyor?: boolean;
  ikon?: ReactNode;
}

/**
 * TEHLİKE ve ONAY dolgusuz çizilir (Linear §6.11 "Danger" kalıbı): şeffaf
 * zemin, renkli metin, %40 opaklıkta kenarlık. Bu yalnızca estetik değil —
 * dolu kırmızı/yeşil zeminlerin üstündeki beyaz yazı koyu temada 2.4:1'e
 * düşerdi (WCAG 1.4.3 ihlali). Dolgu YALNIZCA aksanda kullanılır; aksan tema
 * bağımsız olduğu için üstündeki beyaz yazı her iki temada da 4.9:1'dir.
 */
const BUTON_SINIF: Record<ButonTuru, string> = {
  birincil:
    "bg-simit text-dolgu-ustu border-transparent hover:bg-simit-parlak active:bg-simit-basili disabled:bg-cetvel-koyu disabled:text-murekkep-3",
  ikincil:
    "bg-kagit-2 text-murekkep border-cetvel hover:bg-kagit-3 hover:border-cetvel-koyu active:bg-kagit-3",
  sessiz:
    "bg-transparent text-murekkep-2 border-transparent hover:bg-kagit-2 hover:text-murekkep active:bg-kagit-3",
  tehlike:
    "bg-transparent text-hata border-hata-cizgi hover:bg-hata-yumusak hover:border-hata active:bg-hata-yumusak",
  onay: "bg-transparent text-onay border-onay-cizgi hover:bg-onay-yumusak hover:border-onay active:bg-onay-yumusak",
};

export const Buton = forwardRef<HTMLButtonElement, ButonOzellikleri>(
  function Buton(
    {
      tur = "ikincil",
      kasa = false,
      tamGenislik = false,
      yukleniyor = false,
      ikon,
      children,
      className = "",
      disabled,
      ...kalan
    },
    ref,
  ) {
    const boyut = kasa
      ? "min-h-kasa min-w-kasa px-5 text-lg font-semibold"
      : "min-h-kontrol px-3 py-1 text-md font-satir";
    return (
      <button
        ref={ref}
        disabled={disabled || yukleniyor}
        aria-busy={yukleniyor || undefined}
        className={[
          "inline-flex items-center justify-center gap-1.5 rounded-md border font-govde",
          "transition-colors duration-defter ease-orta",
          "disabled:cursor-not-allowed disabled:opacity-60",
          boyut,
          BUTON_SINIF[tur],
          tamGenislik ? "w-full" : "",
          className,
        ].join(" ")}
        {...kalan}
      >
        {yukleniyor ? <Donen /> : ikon}
        {children}
      </button>
    );
  },
);

function Donen() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="animate-nabiz"
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.3"
      />
      <path
        d="M8 2a6 6 0 0 1 6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * 28x28 hayalet ikon butonu — sayfa başlığındaki görünüm denetimleri,
 * satır eylemleri ve kapatma düğmeleri. Görsel glif 16px olsa bile dokunma
 * hedefi 28px'in altına DÜŞMEZ (erişilebilirlik tabanı).
 */
export const IkonButon = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { etiket: string }
>(function IkonButon({ etiket, children, className = "", ...kalan }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={etiket}
      title={etiket}
      className={[
        "inline-flex h-kontrol w-kontrol min-h-kontrol shrink-0 items-center justify-center rounded-md",
        "text-murekkep-2 transition-colors duration-defter ease-orta",
        "hover:bg-kagit-2 hover:text-murekkep active:bg-kagit-3",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      ].join(" ")}
      {...kalan}
    >
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Kart — gruplanmış ayar kartı
// ---------------------------------------------------------------------------

export interface KartOzellikleri extends HTMLAttributes<HTMLDivElement> {
  baslik?: ReactNode;
  ustBilgi?: ReactNode;
  altBilgi?: ReactNode;
  sikisik?: boolean;
}

export function Kart({
  baslik,
  ustBilgi,
  altBilgi,
  sikisik = false,
  children,
  className = "",
  ...kalan
}: KartOzellikleri) {
  return (
    <section
      className={`overflow-hidden rounded-lg border border-cetvel bg-yuzey ${className}`}
      {...kalan}
    >
      {(baslik || ustBilgi) && (
        <header className="flex min-h-satir items-center justify-between gap-3 border-b border-cetvel-silik px-4 py-2.5">
          {typeof baslik === "string" ? (
            <h2 className="m-0 text-lg font-bolum">{baslik}</h2>
          ) : (
            baslik
          )}
          {ustBilgi}
        </header>
      )}
      <div className={sikisik ? "p-3" : "p-4"}>{children}</div>
      {altBilgi && (
        <footer className="border-t border-cetvel-silik px-4 py-2.5 text-sm text-murekkep-2">
          {altBilgi}
        </footer>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Rozet — çip
// ---------------------------------------------------------------------------

export type RozetTuru = "notr" | "onay" | "uyari" | "hata" | "bilgi" | "simit";

/**
 * 20px yüksekliğinde, hap köşeli, ŞEFFAF dolgulu, 1px kenarlıklı çip.
 * Anlam ASLA yalnızca hueyle taşınmaz: her çip nokta + METİN olarak çizilir
 * (WCAG 1.4.1). Nokta ham etiket huesidir, metin ise okunabilir türevidir.
 */
const ROZET_SINIF: Record<RozetTuru, { cerceve: string; nokta: string }> = {
  notr: { cerceve: "border-cetvel text-murekkep-2", nokta: "bg-murekkep-3" },
  onay: { cerceve: "border-onay-cizgi text-onay", nokta: "bg-etiket-yesil" },
  uyari: { cerceve: "border-uyari-cizgi text-uyari", nokta: "bg-etiket-sari" },
  hata: { cerceve: "border-hata-cizgi text-hata", nokta: "bg-etiket-kirmizi" },
  bilgi: {
    cerceve: "border-bilgi-cizgi text-bilgi",
    nokta: "bg-etiket-indigo",
  },
  simit: {
    cerceve: "border-simit-cizgi text-simit-metin",
    nokta: "bg-etiket-indigo",
  },
};

export function Rozet({
  tur = "notr",
  nokta = true,
  children,
  className = "",
}: {
  tur?: RozetTuru;
  /** Noktayı gizler — sayaç gibi tek başına anlamlı içerikler için. */
  nokta?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const s = ROZET_SINIF[tur];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-tam border bg-transparent px-1.5 py-0.5 text-2xs font-satir leading-none ${s.cerceve} ${className}`}
    >
      {nokta && (
        <span
          aria-hidden="true"
          className={`inline-block h-1.5 w-1.5 shrink-0 rounded-tam ${s.nokta}`}
        />
      )}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Uyarı kutusu
// ---------------------------------------------------------------------------

const UYARI_SINIF: Record<RozetTuru, string> = {
  notr: "border-cetvel bg-kagit-2",
  onay: "border-onay-cizgi bg-onay-yumusak",
  uyari: "border-uyari-cizgi bg-uyari-yumusak",
  hata: "border-hata-cizgi bg-hata-yumusak",
  bilgi: "border-bilgi-cizgi bg-bilgi-yumusak",
  simit: "border-simit-cizgi bg-simit-yumusak",
};

const UYARI_NOKTA: Record<RozetTuru, string> = {
  notr: "bg-murekkep-3",
  onay: "bg-etiket-yesil",
  uyari: "bg-etiket-sari",
  hata: "bg-etiket-kirmizi",
  bilgi: "bg-etiket-indigo",
  simit: "bg-etiket-indigo",
};

export function Uyari({
  tur = "bilgi",
  baslik,
  children,
  className = "",
}: {
  tur?: RozetTuru;
  baslik?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tur === "hata" ? "alert" : "status"}
      /* Kenarlık 1px — HER ZAMAN 1px. Eski 4px'lik sol şerit kaldırıldı. */
      className={`flex gap-2.5 rounded-md border px-3 py-2.5 ${UYARI_SINIF[tur]} ${className}`}
    >
      <span
        aria-hidden="true"
        className={`mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-tam ${UYARI_NOKTA[tur]}`}
      />
      <div className="min-w-0 flex-1">
        {baslik && <p className="m-0 font-satir text-murekkep">{baslik}</p>}
        {children && (
          <div className={`text-sm text-murekkep-2 ${baslik ? "mt-0.5" : ""}`}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form alanları
// ---------------------------------------------------------------------------

/** Girdi kabuğu — alan, seçici ve metin alanı aynı gövdeyi paylaşır. */
const GIRDI_TABAN =
  "w-full rounded-md border bg-girdi px-2.5 font-govde text-md text-murekkep " +
  "placeholder:text-murekkep-3 transition-colors duration-defter ease-orta " +
  "disabled:cursor-not-allowed disabled:opacity-60";

const GIRDI_CERCEVE =
  "border-cetvel hover:border-cetvel-koyu focus:border-simit";

export interface AlanOzellikleri extends InputHTMLAttributes<HTMLInputElement> {
  etiket: string;
  ipucu?: string;
  hata?: string;
  kasa?: boolean;
}

export const Alan = forwardRef<HTMLInputElement, AlanOzellikleri>(function Alan(
  { etiket, ipucu, hata, kasa = false, className = "", id, ...kalan },
  ref,
) {
  const kendiId = useId();
  const alanId = id ?? kendiId;
  const ipucuId = `${alanId}-ipucu`;
  const hataId = `${alanId}-hata`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={alanId} className="text-sm font-satir text-murekkep">
        {etiket}
      </label>
      <input
        ref={ref}
        id={alanId}
        aria-invalid={hata ? true : undefined}
        aria-describedby={
          [ipucu ? ipucuId : null, hata ? hataId : null]
            .filter(Boolean)
            .join(" ") || undefined
        }
        className={[
          GIRDI_TABAN,
          kasa ? "min-h-kasa text-lg" : "min-h-hedef py-1",
          hata
            ? "border-hata hover:border-hata focus:border-hata"
            : GIRDI_CERCEVE,
          className,
        ].join(" ")}
        {...kalan}
      />
      {ipucu && !hata && (
        <p id={ipucuId} className="m-0 text-xs text-murekkep-3">
          {ipucu}
        </p>
      )}
      {hata && (
        <p id={hataId} className="m-0 text-xs text-hata" role="alert">
          {hata}
        </p>
      )}
    </div>
  );
});

export interface SecimOzellikleri extends SelectHTMLAttributes<HTMLSelectElement> {
  etiket: string;
  secenekler: { deger: string; metin: string }[];
}

export const Secim = forwardRef<HTMLSelectElement, SecimOzellikleri>(
  function Secim({ etiket, secenekler, className = "", id, ...kalan }, ref) {
    const kendiId = useId();
    const alanId = id ?? kendiId;
    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={alanId} className="text-sm font-satir text-murekkep">
          {etiket}
        </label>
        <select
          ref={ref}
          id={alanId}
          className={`${GIRDI_TABAN} ${GIRDI_CERCEVE} min-h-hedef py-1 ${className}`}
          {...kalan}
        >
          {secenekler.map((s) => (
            <option key={s.deger} value={s.deger}>
              {s.metin}
            </option>
          ))}
        </select>
      </div>
    );
  },
);

/**
 * Anahtar — 32x18 ray, 14px topuz, 2px iç boşluk.
 * Kapalı ray `--cetvel-koyu`, açık ray aksan. 140ms.
 */
export function Anahtar({
  acik,
  degistir,
  etiket,
  aciklama,
  devreDisi = false,
  id,
}: {
  acik: boolean;
  degistir: (yeni: boolean) => void;
  etiket: string;
  aciklama?: ReactNode;
  devreDisi?: boolean;
  id?: string;
}) {
  const kendiId = useId();
  const anahtarId = id ?? kendiId;
  const aciklamaId = `${anahtarId}-aciklama`;
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <label
          htmlFor={anahtarId}
          className="block font-satir text-md text-murekkep"
        >
          {etiket}
        </label>
        {aciklama && (
          <p id={aciklamaId} className="m-0 mt-0.5 text-sm text-murekkep-2">
            {aciklama}
          </p>
        )}
      </div>
      <button
        id={anahtarId}
        type="button"
        role="switch"
        aria-checked={acik}
        aria-describedby={aciklama ? aciklamaId : undefined}
        disabled={devreDisi}
        onClick={() => degistir(!acik)}
        className={[
          "relative inline-flex h-[18px] w-8 shrink-0 items-center rounded-tam p-0.5",
          "transition-colors duration-hizli ease-orta disabled:cursor-not-allowed disabled:opacity-50",
          acik ? "bg-simit" : "bg-cetvel-koyu",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "block h-3.5 w-3.5 rounded-tam bg-dolgu-ustu",
            "transition-transform duration-hizli ease-orta",
            acik ? "translate-x-[14px]" : "translate-x-0",
          ].join(" ")}
        />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tablo — yoğun liste
// ---------------------------------------------------------------------------

export function Tablo({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`overflow-x-auto rounded-lg border border-cetvel bg-yuzey ${className}`}
    >
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

/**
 * Grup başlığı satırı: 12px/550, zemin bir basamak yükselir ve liste
 * genişliği boyunca bir BANT olarak okunur.
 */
export function TabloBaslik({ children }: { children: ReactNode }) {
  return (
    <thead className="bg-kagit-2 text-murekkep-2">
      <tr className="[&>th]:h-8 [&>th]:border-b [&>th]:border-cetvel-silik [&>th]:px-3 [&>th]:text-left [&>th]:text-xs [&>th]:font-bolum [&>th]:tracking-normal">
        {children}
      </tr>
    </thead>
  );
}

/** Satır 40px, tam genişlik, üzerine gelince YALNIZCA zemin değişir. */
export function TabloGovde({ children }: { children: ReactNode }) {
  return (
    <tbody className="[&>tr]:border-b [&>tr]:border-cetvel-silik [&>tr:last-child]:border-0 [&>tr]:transition-colors [&>tr]:duration-defter [&>tr:hover]:bg-kagit-2 [&_td]:h-satir [&_td]:px-3 [&_td]:py-2 [&_td]:align-middle">
      {children}
    </tbody>
  );
}

// ---------------------------------------------------------------------------
// Boş durum ve yükleme
// ---------------------------------------------------------------------------

/**
 * Boş durum: 96px çizgisel çizim `--cetvel-koyu` tonunda, altında tek cümle.
 * Noktalama yok, sıçrayan animasyon yok. Eylem varsa TEK affordans olarak durur.
 */
export function BosDurum({
  baslik,
  aciklama,
  eylem,
}: {
  baslik: string;
  aciklama?: string;
  eylem?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <svg
        width="96"
        height="96"
        viewBox="0 0 96 96"
        aria-hidden="true"
        className="text-cetvel-koyu"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="18" y="14" width="60" height="68" rx="6" />
        <path d="M30 32h36M30 46h36M30 60h22" />
      </svg>
      <p className="m-0 font-satir text-md text-murekkep-2">{baslik}</p>
      {aciklama && (
        <p className="m-0 max-w-icerik text-sm text-murekkep-3">{aciklama}</p>
      )}
      {eylem && <div className="mt-1">{eylem}</div>}
    </div>
  );
}

export function Iskelet({ satir = 3 }: { satir?: number }) {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Yükleniyor">
      {Array.from({ length: satir }).map((_, i) => (
        <div key={i} className="h-4 animate-nabiz rounded-sm bg-kagit-3" />
      ))}
      <span className="gizli-erisilebilir">Yükleniyor</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kişi avatarı — ADR-17 / KK-536
// ---------------------------------------------------------------------------

/**
 * Sabit 8 hueli etiket paleti — avatar dolgusu ada göre DETERMİNİSTİK seçilir.
 *
 * Baş harfin rengi hue ile BİRLİKTE taşınır: sarı, camgöbeği ve yeşil dolgular
 * üzerinde beyaz yazı 2:1'in altında kalır. Koyu mürekkep bu üçünde >= 7:1,
 * beyaz ise kalan beşinde >= 4.5:1 verir.
 */
const AVATAR_HUE: readonly { zemin: string; yazi: string }[] = [
  { zemin: "var(--etiket-indigo)", yazi: "#FFFFFF" },
  { zemin: "var(--etiket-mor)", yazi: "#1B0F33" },
  { zemin: "var(--etiket-camgobegi)", yazi: "#04262B" },
  { zemin: "var(--etiket-yesil)", yazi: "#0A2A1D" },
  { zemin: "var(--etiket-turuncu)", yazi: "#3A2003" },
  { zemin: "var(--etiket-kirmizi)", yazi: "#FFFFFF" },
  { zemin: "var(--etiket-sari)", yazi: "#3A2E05" },
  { zemin: "var(--etiket-pembe)", yazi: "#FFFFFF" },
];

function hueSec(anahtar: string): { zemin: string; yazi: string } {
  let toplam = 0;
  for (let i = 0; i < anahtar.length; i += 1) {
    toplam = (toplam * 31 + anahtar.charCodeAt(i)) >>> 0;
  }
  return AVATAR_HUE[toplam % AVATAR_HUE.length]!;
}

/**
 * Fotoğraf ZORUNLU DEĞİLDİR. Yoksa ekran düzeni BOZULMAZ ve "fotoğraf yok"
 * hatası VERİLMEZ; baş harflerden oluşan bir avatar ve büyük puntolu ad-soyad
 * gösterilir. Bu, ADR-17'nin doğrudan arayüz karşılığıdır.
 *
 * Dolgu rengi addan türetilir ve SABİTTİR: aynı kişi her ekranda aynı renkte
 * görünür, böylece renk ikinci bir tanıma ipucu olur (ama TEK ipucu değildir —
 * baş harfler her zaman yazılır).
 */
export function Avatar({
  ad,
  soyad,
  fotoUrl,
  boyut = 64,
}: {
  ad: string;
  soyad: string;
  fotoUrl?: string | null;
  boyut?: number;
}) {
  // Baş harf avatarı TEMELDİR, fotoğraf onun üstüne serilir. Sıra bilerek
  // böyle: bir adresin VAR OLMASI, içeriğin GELECEĞİ anlamına gelmiyor.
  // 0015 öncesi serbest metin adresler ölü bir hosta bakabiliyor
  // (kantin.local gibi); böyle bir istek hata bile vermez, DNS'te asılı kalır
  // ve `onError` HİÇ tetiklenmez. Fotoğrafı önce koyup hatayı beklemek, o
  // kayıtlarda ekranda süresiz boş bir kutu bırakıyordu.
  //
  // Bu kurulumda fotoğraf yalnızca `load` sonrası görünür olur: gelmezse,
  // geç gelirse ya da hata verirse kullanıcı zaten doğru şeyi görüyordur ve
  // hiçbir an boş kalmaz.
  const [yuklendi, setYuklendi] = useState(false);
  const [yuklenemedi, setYuklenemedi] = useState(false);
  useEffect(() => {
    setYuklendi(false);
    setYuklenemedi(false);
  }, [fotoUrl]);

  const basHarfler = `${ad.charAt(0)}${soyad.charAt(0)}`.toLocaleUpperCase(
    "tr",
  );
  const hue = hueSec(`${ad} ${soyad}`);

  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-lg"
      style={{ width: boyut, height: boyut }}
    >
      <div
        aria-hidden="true"
        className="flex h-full w-full select-none items-center justify-center font-bolum"
        style={{
          fontSize: Math.max(11, boyut * 0.36),
          letterSpacing: "0.02em",
          background: hue.zemin,
          color: hue.yazi,
        }}
      >
        {basHarfler}
      </div>
      {fotoUrl && !yuklenemedi ? (
        <img
          src={fotoUrl}
          alt={`${ad} ${soyad}`}
          width={boyut}
          height={boyut}
          onLoad={() => setYuklendi(true)}
          onError={() => setYuklenemedi(true)}
          className="absolute inset-0 h-full w-full rounded-lg border border-cetvel object-cover"
          style={{ visibility: yuklendi ? "visible" : "hidden" }}
        />
      ) : null}
    </div>
  );
}
